/**
 * Read-only "View as agent" endpoints (D2-SEC-011, #3944).
 *
 * The server is the gate: only a super-user or a manager with oversight may
 * start a session, only for an agent ranked below them, and every write is
 * refused while it runs. See `lib/viewAs.ts` for how the token travels.
 */
import { apiRequest } from "./client";
import {
  clearViewAs,
  getStoredViewAsToken,
  setViewAs,
} from "../lib/viewAs";
import type { ViewAsPerson, ViewAsState } from "../lib/viewAs";

/** The agents the caller may view as. Rejects (403) for anyone not eligible. */
export async function getViewAsTargets(
  signal?: AbortSignal,
): Promise<ViewAsPerson[]> {
  const body = await apiRequest<{ results: ViewAsPerson[] }>(
    "/auth/view-as/targets/",
    { method: "GET" },
    signal,
  );
  return body.results;
}

/** Start viewing as `targetId` and remember the session for this tab. */
export async function startViewAs(targetId: number): Promise<ViewAsState> {
  const state = await apiRequest<ViewAsState & { read_only: boolean }>(
    "/auth/view-as/start/",
    { method: "POST", body: JSON.stringify({ target_id: targetId }) },
  );
  const stored: ViewAsState = {
    token: state.token,
    expires_at: state.expires_at,
    viewer: state.viewer,
    target: state.target,
  };
  setViewAs(stored);
  return stored;
}

/**
 * Start viewing as `targetId`, then reload: the whole app re-fetches as the
 * agent (the server answers as them for every call carrying the token) and
 * lands on the agent's home.
 */
export async function beginViewAs(targetId: number): Promise<void> {
  await startViewAs(targetId);
  window.location.assign("/");
}

/** End the session on the server (audited) and forget it. Best-effort. */
export async function stopViewAs(): Promise<void> {
  if (!getStoredViewAsToken()) return;
  try {
    // The fetch wrapper attaches the X-View-As header the server reads.
    await apiRequest("/auth/view-as/stop/", {
      method: "POST",
      body: JSON.stringify({}),
    });
  } catch {
    // The session still dies on its own at expiry; never trap the viewer.
  } finally {
    clearViewAs();
  }
}
